import { useState, useCallback, useMemo, useLayoutEffect, useEffect } from 'react';

import applyDocumentLanguage, {
    normalizeLanguage,
    FALLBACK_LANGUAGE,
} from '../util/apply-document-language';
import isGlobalAppLanguageRoute from '../util/is-global-app-language-route';
import isStoreLanguageRoute from '../util/is-store-language-route';
import getStoreUsernameFromPathname from '../util/get-store-username-from-pathname';

const GLOBAL_LANGUAGE_STORAGE_KEY = 'storec-ui-language';

const getStoreLanguageStorageKey = (storeUsername) => (
    storeUsername ? `storec-store-language-${storeUsername}` : null
);

const readStoredGlobalLanguage = () => {
    try {
        const stored = sessionStorage.getItem(GLOBAL_LANGUAGE_STORAGE_KEY);
        return stored ? normalizeLanguage(stored) : FALLBACK_LANGUAGE;
    } catch {
        return FALLBACK_LANGUAGE;
    }
};

const persistGlobalLanguage = (language) => {
    try {
        sessionStorage.setItem(GLOBAL_LANGUAGE_STORAGE_KEY, normalizeLanguage(language));
    } catch {
        // ignore storage errors
    }
};

const readStoredStoreLanguage = (storeUsername) => {
    const storageKey = getStoreLanguageStorageKey(storeUsername);

    if (!storageKey) {
        return null;
    }

    try {
        const stored = sessionStorage.getItem(storageKey);
        return stored ? normalizeLanguage(stored) : null;
    } catch {
        return null;
    }
};

const persistStoreLanguage = (storeUsername, language) => {
    const storageKey = getStoreLanguageStorageKey(storeUsername);

    if (!storageKey) {
        return;
    }

    try {
        sessionStorage.setItem(storageKey, normalizeLanguage(language));
    } catch {
        // ignore storage errors
    }
};

const clearStoredStoreLanguage = (storeUsername) => {
    const storageKey = getStoreLanguageStorageKey(storeUsername);

    if (!storageKey) {
        return;
    }

    try {
        sessionStorage.removeItem(storageKey);
    } catch {
        // ignore storage errors
    }
};

const resolveStoreDefaultLanguage = (storeSettings) => {
    if (storeSettings?.multiLanguageEnabled === true) {
        return normalizeLanguage(storeSettings?.defaultLanguage);
    }

    return FALLBACK_LANGUAGE;
};

const resolveStoreLanguageFromSettings = (storeSettings, settingsStoreUsername) => {
    if (!settingsStoreUsername) {
        return FALLBACK_LANGUAGE;
    }

    if (storeSettings?.multiLanguageEnabled !== true) {
        return FALLBACK_LANGUAGE;
    }

    return readStoredStoreLanguage(settingsStoreUsername)
        ?? resolveStoreDefaultLanguage(storeSettings);
};

/**
 * Centralized language state: always on Storec-wide routes; on store routes only after settings load.
 *
 * @param {{ storeSettings?: { multiLanguageEnabled?: boolean, defaultLanguage?: string } | null, pathname?: string, isStoreSettingsReady?: boolean, settingsStoreUsername?: string }} options
 */
const useLanguage = ({
    storeSettings = null,
    pathname = '',
    isStoreSettingsReady = false,
    settingsStoreUsername = '',
} = {}) => {
    const [globalLanguage, setGlobalLanguage] = useState(() => readStoredGlobalLanguage());
    const [storeLanguageOverride, setStoreLanguageOverride] = useState(null);

    const isGlobalAppRoute = isGlobalAppLanguageRoute(pathname);
    const isStoreRoute = isStoreLanguageRoute(pathname);
    const routeStoreUsername = getStoreUsernameFromPathname(pathname);
    const multiLanguageEnabled = storeSettings?.multiLanguageEnabled === true;
    const defaultLanguage = multiLanguageEnabled
        ? normalizeLanguage(storeSettings?.defaultLanguage)
        : FALLBACK_LANGUAGE;

    const isStoreSettingsReadyForRoute = useMemo(() => {
        if (!isStoreSettingsReady || !settingsStoreUsername) {
            return false;
        }

        if (routeStoreUsername) {
            return settingsStoreUsername === routeStoreUsername;
        }

        return true;
    }, [isStoreSettingsReady, settingsStoreUsername, routeStoreUsername]);

    const isStoreLocaleReady = !isStoreRoute || isStoreSettingsReadyForRoute;
    const isLanguageSwitchingEnabled = isGlobalAppRoute
        || (isStoreRoute && multiLanguageEnabled && isStoreLocaleReady);

    useEffect(() => {
        setStoreLanguageOverride(null);
    }, [settingsStoreUsername, routeStoreUsername, multiLanguageEnabled]);

    useEffect(() => {
        if (!multiLanguageEnabled && settingsStoreUsername) {
            clearStoredStoreLanguage(settingsStoreUsername);
        }
    }, [multiLanguageEnabled, settingsStoreUsername]);

    const currentLanguage = useMemo(() => {
        if (isGlobalAppRoute) {
            return globalLanguage;
        }

        if (isStoreRoute) {
            if (!isStoreLocaleReady) {
                return FALLBACK_LANGUAGE;
            }

            if (storeLanguageOverride) {
                return storeLanguageOverride;
            }

            return resolveStoreLanguageFromSettings(storeSettings, settingsStoreUsername);
        }

        return FALLBACK_LANGUAGE;
    }, [
        isGlobalAppRoute,
        isStoreRoute,
        isStoreLocaleReady,
        globalLanguage,
        storeLanguageOverride,
        storeSettings,
        settingsStoreUsername,
    ]);

    useLayoutEffect(() => {
        if (isStoreRoute && !isStoreLocaleReady) {
            return;
        }

        applyDocumentLanguage(currentLanguage);
    }, [currentLanguage, isStoreRoute, isStoreLocaleReady]);

    const setLanguage = useCallback((language) => {
        const normalizedLanguage = normalizeLanguage(language);

        if (isGlobalAppRoute) {
            setGlobalLanguage(normalizedLanguage);
            persistGlobalLanguage(normalizedLanguage);
            return;
        }

        if (isStoreRoute && multiLanguageEnabled && isStoreLocaleReady && settingsStoreUsername) {
            setStoreLanguageOverride(normalizedLanguage);
            persistStoreLanguage(settingsStoreUsername, normalizedLanguage);
        }
    }, [
        isGlobalAppRoute,
        isStoreRoute,
        multiLanguageEnabled,
        isStoreLocaleReady,
        settingsStoreUsername,
    ]);

    return {
        currentLanguage,
        defaultLanguage,
        multiLanguageEnabled,
        isLanguageSwitchingEnabled,
        isGlobalAppRoute,
        isStoreRoute,
        isStoreLocaleReady,
        isStoreLanguageReady: isStoreLocaleReady,
        routeStoreUsername,
        setLanguage,
    };
};

export default useLanguage;
